import "./journal-section-first-filter.scss";
import { type Dispatch, type SetStateAction, useState } from "react";
import { getStatusWithIcon } from "@/entities/journal/helpers/status-map";
import {
  FiltersMenuDate,
  FiltersMenuRow,
  FiltersMenuTags,
  JournalActiveFilterChips,
  JournalFiltersMenu,
  formatJournalFilterDate,
  type JournalActiveFilterChip,
} from "@/features/journal-filters-menu";
import { FiltersButton, InputSearchWithInfo, type IStatus } from "@shared/ui";

// BUILDDOCS-4959: скрыто до реализации на бэкенде
// const DISPLAY_OPTIONS = [
//   { id: "FAVORITE", label: "Избранное" },
//   { id: "ACTIVE", label: "Активное" },
//   { id: "DELETED", label: "Удаленное" },
//   { id: "ALL", label: "Все" },
// ];

const STATUS_OPTIONS = [
  { id: "NEW", label: "Новый" },
  { id: "AWAITING_SIGNATURE", label: "На подписании" },
  { id: "SIGNED", label: "Подписан" },
  { id: "SENT_TO_GSN", label: "Отправлено" },
  { id: "REJECTED", label: "Отклонено" },
];

type Signer = {
  representativeId?: string;
  constructionControlId?: string;
  user?: {
    userId?: string;
    surname?: string;
    firstName?: string;
    middleName?: string;
  };
};

type Draft = {
  display: string[];
  statuses: string[];
  signers: string[];
  workPeriod: [string, string];
  creationDate: string | null;
  lastChangeDate: string | null;
};

const emptyDraft = (): Draft => ({
  display: [],
  statuses: [],
  signers: [],
  workPeriod: ["", ""],
  creationDate: null,
  lastChangeDate: null,
});

// BUILDDOCS-4959: скрыто до реализации на бэкенде
// const formatSignerLabel = (signer: Signer) => {
//   const surname = signer.user?.surname || "";
//   const firstInitial = signer.user?.firstName ? `${signer.user.firstName[0]}.` : "";
//   const middleInitial = signer.user?.middleName ? `${signer.user.middleName[0]}.` : "";
//   return `${surname} ${firstInitial}${middleInitial}`.trim();
// };

// const getSignerId = (signer: Signer) =>
//   signer.representativeId ??
//   signer.constructionControlId ??
//   signer.user?.userId ??
//   "";

export const JournalSectionFirstFilter = ({
  setSearch,
  setDateStart,
  setDateEnd,
  dateStart,
  dateEnd,
  selectedStatus,
  setSelectedStatus,
  onFiltersApply,
  bulkSendMode = false,
}: {
  setSearch: Dispatch<SetStateAction<string>>;
  setDateStart: Dispatch<SetStateAction<string>>;
  setDateEnd: Dispatch<SetStateAction<string>>;
  dateStart: string;
  dateEnd: string;
  selectedStatus: IStatus | null;
  setSelectedStatus: Dispatch<SetStateAction<IStatus | null>>;
  // eslint-disable-next-line react/no-unused-prop-types
  signersList: Signer[];
  onFiltersApply?: () => void;
  bulkSendMode?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [uiFilters, setUiFilters] = useState({ display: [] as string[], signers: [] as string[], creationDate: null as string | null, lastChangeDate: null as string | null });
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  // BUILDDOCS-4959: скрыто до реализации на бэкенде
  // const signerOptions = useMemo(
  //   () =>
  //     signersList
  //       .map((signer) => ({
  //         id: getSignerId(signer),
  //         label: formatSignerLabel(signer),
  //       }))
  //       .filter((item) => item.id),
  //   [signersList],
  // );

  const buildDraft = (): Draft => ({
    display: uiFilters.display,
    statuses: selectedStatus ? [selectedStatus.id] : [],
    signers: uiFilters.signers,
    workPeriod: [dateStart, dateEnd],
    creationDate: uiFilters.creationDate,
    lastChangeDate: uiFilters.lastChangeDate,
  });

  const hasActiveFilters =
    (!bulkSendMode && Boolean(selectedStatus)) ||
    Boolean(dateStart || dateEnd);
    // uiFilters.display.length > 0 ||
    // uiFilters.signers.length > 0 ||
    // Boolean(uiFilters.creationDate || uiFilters.lastChangeDate);

  const openModal = () => {
    setDraft(buildDraft());
    setOpen(true);
  };

  const applyFilters = () => {
    const statusId = draft.statuses[0];
    setSelectedStatus(statusId ? getStatusWithIcon(statusId) : null);
    setDateStart(draft.workPeriod[0]);
    setDateEnd(draft.workPeriod[1]);
    setUiFilters({
      display: draft.display,
      signers: draft.signers,
      creationDate: draft.creationDate,
      lastChangeDate: draft.lastChangeDate,
    });
    setOpen(false);
    onFiltersApply?.();
  };

  const resetFilters = () => {
    setDraft(emptyDraft());
    setSelectedStatus(null);
    setDateStart("");
    setDateEnd("");
    setUiFilters({
      display: [],
      signers: [],
      creationDate: null,
      lastChangeDate: null,
    });
    setOpen(false);
    onFiltersApply?.();
  };

  return (
    <div className="journal-section-first-filter">
      <InputSearchWithInfo
        className="journal-section-first-filter__search"
        style={{ fontSize: "12px" }}
        onChange={(e) => setSearch(e.target.value)}
        tooltipTitle="Поиск по наименованию организации"
      />
      <FiltersButton active={hasActiveFilters} onClick={openModal} />

      <JournalFiltersMenu
        open={open}
        onClose={() => setOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
        withDividers={false}
      >
        {/* BUILDDOCS-4959: скрыто до реализации на бэкенде
        <FiltersMenuRow label="Отображение" withDivider={false}>
          <FiltersMenuTags
            options={DISPLAY_OPTIONS}
            value={draft.display}
            onChange={(display) => setDraft((prev) => ({ ...prev, display }))}
          />
        </FiltersMenuRow>
        */}

        {!bulkSendMode ? (
          <FiltersMenuRow
            label="Статус записи"
            withDivider={false}
            onClear={
              draft.statuses.length
                ? () => setDraft((prev) => ({ ...prev, statuses: [] }))
                : undefined
            }
          >
            <FiltersMenuTags
              options={STATUS_OPTIONS}
              value={draft.statuses}
              onChange={(statuses) => setDraft((prev) => ({ ...prev, statuses }))}
            />
          </FiltersMenuRow>
        ) : null}

        {/* BUILDDOCS-4959: скрыто до реализации на бэкенде
        <FiltersMenuRow
          label="Подписант"
          withDivider={false}
          onClear={
            draft.signers.length
              ? () => setDraft((prev) => ({ ...prev, signers: [] }))
              : undefined
          }
        >
          <FiltersMenuTags
            options={signerOptions}
            value={draft.signers}
            onChange={(signers) => setDraft((prev) => ({ ...prev, signers }))}
          />
        </FiltersMenuRow>
        */}

        <FiltersMenuRow label="Период работ" withDivider={false}>
          <FiltersMenuDate
            range
            value={draft.workPeriod}
            onChange={(workPeriod) =>
              setDraft((prev) => ({
                ...prev,
                workPeriod: (workPeriod as [string, string]) || ["", ""],
              }))
            }
          />
        </FiltersMenuRow>

        {/* BUILDDOCS-4959: скрыто до реализации на бэкенде
        <FiltersMenuRow label="Дата создания" withDivider={false}>
          <FiltersMenuDate
            value={draft.creationDate}
            onChange={(creationDate) =>
              setDraft((prev) => ({
                ...prev,
                creationDate: creationDate as string | null,
              }))
            }
          />
        </FiltersMenuRow>

        <FiltersMenuRow label="Дата последнего изменения" withDivider={false}>
          <FiltersMenuDate
            value={draft.lastChangeDate}
            onChange={(lastChangeDate) =>
              setDraft((prev) => ({
                ...prev,
                lastChangeDate: lastChangeDate as string | null,
              }))
            }
          />
        </FiltersMenuRow>
        */}
      </JournalFiltersMenu>
    </div>
  );
};

export const JournalSectionFirstActiveFilterChips = ({
  selectedStatus,
  setSelectedStatus,
  dateStart,
  dateEnd,
  setDateStart,
  setDateEnd,
  onFilterRemove,
  bulkSendMode = false,
}: {
  selectedStatus: IStatus | null;
  setSelectedStatus: Dispatch<SetStateAction<IStatus | null>>;
  dateStart: string;
  dateEnd: string;
  setDateStart: Dispatch<SetStateAction<string>>;
  setDateEnd: Dispatch<SetStateAction<string>>;
  onFilterRemove?: () => void;
  bulkSendMode?: boolean;
}) => {
  const removeFilter = (updater: () => void) => {
    updater();
    onFilterRemove?.();
  };

  const chips: JournalActiveFilterChip[] = [];

  if (!bulkSendMode && selectedStatus) {
    const option = STATUS_OPTIONS.find((item) => item.id === selectedStatus.id);
    chips.push({
      key: `status-${selectedStatus.id}`,
      filterLabel: "Статус записи",
      value: option?.label || selectedStatus.status,
      onRemove: () => removeFilter(() => setSelectedStatus(null)),
    });
  }

  if (dateStart || dateEnd) {
    let periodValue = "";
    if (dateStart && dateEnd) {
      periodValue = `${formatJournalFilterDate(dateStart)} — ${formatJournalFilterDate(dateEnd)}`;
    } else if (dateStart) {
      periodValue = formatJournalFilterDate(dateStart);
    } else {
      periodValue = formatJournalFilterDate(dateEnd);
    }
    chips.push({
      key: "work-period",
      filterLabel: "Период работ",
      value: periodValue,
      onRemove: () =>
        removeFilter(() => {
          setDateStart("");
          setDateEnd("");
        }),
    });
  }

  return <JournalActiveFilterChips chips={chips} />;
};
